import React, { useState } from 'react';
import { Dialog, DialogContent, Box, Typography, Divider, Grid, useMediaQuery } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from "@mui/material/styles";
import { useSelector } from 'react-redux';
import ContentPackCoursePreviewModal from '../../../content-packs/ContentPackCoursePreviewModal';
import ContentPackCardComponent from '../../../../components/cards/ContentPackCard';
import { CalendarSimulationPreviewModal } from './CalendarSimulationPreview';
import SimulationCollectionPreview from '../../../sim-collections/SimulationCollectionPreview';
import { useModalStack } from '../../../../components/Modal/ModalStackContext';
import { ModalProvider } from '../../../../components/Modal/ModalContext';
import { useGetContentPackByIdQuery, useGetValidCoursesQuery } from '../../../../store/services/university-service';
import { getCourseMediaUrls } from '../../../../utils/coursePathUtils';


// Calendar-specific course preview wrapper
export const CalendarCoursePreview = ({ courseId, onClose, style, open = true, ...props }) => {
    return (
        <ModalProvider>
            <ContentPackCoursePreviewModal
                {...props}
                courseId={courseId}
                context="calendar"
                onClose={onClose}
                open={open} // FIXED: Use the open prop from modal stack
                style={style} // Pass through z-index from modal stack
                />
        </ModalProvider>
    );
};

// Calendar-specific simulation preview wrapper
export const CalendarSimulationPreview = ({ simulationId, onClose, style, open = true, ...props }) => {
    return (
        <CalendarSimulationPreviewModal
            {...props}
            simulationId={simulationId}
            onClose={onClose}
            open={open} // FIXED: Use the open prop from modal stack
            showBackdrop={false}
            isStandalone={true}
            style={style} // Pass through z-index from modal stack
        />
    );
};

// FIXED: Calendar-specific collection preview wrapper - now includes Dialog wrapper
export const CalendarCollectionPreview = ({ collectionId, onClose, style, open = true, ...props }) => {
    const { pushModal } = useModalStack();

    const handleNestedSimulationPreview = (simulationId) => {
        pushModal({
            type: 'simulationPreview',
            props: { simulationId }
        });
    };

    // Get z-index from modal stack or use default
    const zIndexValue = style?.zIndex || 1300;

    return (
        <Dialog
            open={open} // FIXED: Use the open prop from modal stack instead of hardcoded true
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            // Apply z-index at Dialog level to ensure proper layering
            style={{ zIndex: zIndexValue }}
            PaperProps={{
                sx: {
                    width: '70%',
                    height: '80%',
                    margin: '0 auto',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    position: 'relative',
                }
            }}
        >
            <DialogContent sx={{ p: 0, height: '100%' }}>
                <ModalProvider>
                <SimulationCollectionPreview
                    {...props}
                    collectionId={collectionId}
                    onNestedSimulationPreview={handleNestedSimulationPreview}
                    context="calendar"
                    />
                </ModalProvider>
            </DialogContent>
        </Dialog>
    );
};

// Animation variants for content pack preview
const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.35 } },
    exit: { opacity: 0, transition: { duration: 0.35 } }
};

const cardVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            type: 'spring',
            stiffness: 100,
            damping: 15,
            duration: 0.35
        }
    }
};

const DEFAULT_GIF = '/default.gif';
const DEFAULT_IMG = '/default.png';

// Calendar-specific content pack preview wrapper with modal stack integration
export const CalendarContentPackPreview = ({ contentPackId, onClose, style, open = true, ...props }) => {
    const { pushModal } = useModalStack();
    const theme = useTheme();
    const { tenancyId } = useSelector((state) => state.tenancy);
    const matchDownSM = useMediaQuery(theme.breakpoints.down('sm'));

    // Get z-index from modal stack or use default
    const zIndexValue = style?.zIndex || 1300;

    const {
        data: contentPack,
        isLoading: contentPackLoading,
        isError: contentPackError
    } = useGetContentPackByIdQuery(contentPackId);

    const {
        data: coursesData,
        isLoading: coursesLoading,
        error: coursesError
    } = useGetValidCoursesQuery({
        perPage: 10000,
        page: 1,
        includes: '',
        query: '',
        additionalQueryParams: `&published=true&tenancyId=${tenancyId}`
    });

    // Handle course preview by pushing to modal stack
    const handleNestedCoursePreview = (courseId) => {
        pushModal({
            type: 'coursePreview',
            props: { courseId }
        });
    };

    const loading = contentPackLoading || coursesLoading;
    const error = contentPackError || coursesError;

    const getGridItemSize = () => {
        if (matchDownSM) return 12;
        return 6;
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            style={{ zIndex: zIndexValue }}
            PaperProps={{
                sx: {
                    width: '70%',
                    height: '80%',
                    margin: '0 auto',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    position: 'relative',
                }
            }}
        >
            <DialogContent sx={{ p: 0, height: '100%' }}>
                {loading ? (
                    <Box p={2}>
                        <Typography>Loading content pack...</Typography>
                    </Box>
                ) : error || !contentPack ? (
                    <Box p={2}>
                        <Typography color="error">Content pack not found</Typography>
                    </Box>
                ) : (
                    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <Box p={2} sx={{ flexShrink: 0 }}>
                            <Typography variant="h2" mb={2}>
                                {contentPack.contentPackName}
                            </Typography>
                            <Divider sx={{ width: "100%", mb: 2 }} />
                        </Box>

                        <Box p={2} pt={0} sx={{
                            overflowY: 'auto',
                            flexGrow: 1,
                            '&::-webkit-scrollbar': {
                                width: '8px',
                            },
                            '&::-webkit-scrollbar-track': {
                                backgroundColor: theme.palette.background.default,
                            },
                            '&::-webkit-scrollbar-thumb': {
                                backgroundColor: theme.palette.divider,
                                borderRadius: '4px',
                            },
                            '&::-webkit-scrollbar-thumb:hover': {
                                backgroundColor: theme.palette.action.hover,
                            },
                        }}>
                            <Grid container spacing={2} justifyContent="center" alignItems="center">
                                <Grid item xs={12}>
                                    <AnimatePresence mode="wait">
                                        <motion.div
                                            key="content-pack-preview"
                                            initial="hidden"
                                            animate="visible"
                                            exit="exit"
                                            variants={containerVariants}
                                        >
                                            <Grid container spacing={2} justifyContent="center" alignItems="stretch">
                                                {contentPack.courseIds?.map((courseId) => {
                                                    const courseData = coursesData?.find((c) => c.id === courseId);

                                                    const { imageUrl, gifUrl } = getCourseMediaUrls(
                                                        courseData?.courseContentPath,
                                                        courseData?.isLms
                                                    );

                                                    const course_img = imageUrl || DEFAULT_IMG;
                                                    const course_gif = gifUrl || DEFAULT_GIF;

                                                    return (
                                                        <Grid item xs={12} sm={getGridItemSize()} key={courseId} sx={{ mb: 4 }}>
                                                            <motion.div
                                                                variants={cardVariants}
                                                                initial="hidden"
                                                                animate="visible"
                                                                whileHover={{ scale: 1.03, transition: { duration: 0.4 } }}
                                                                style={{ height: '100%' }}
                                                            >
                                                                <ContentPackCardComponent
                                                                    image={course_img}
                                                                    title={courseData?.courseName ? courseData.courseName : "undefined"}
                                                                    gif={course_gif}
                                                                    type="preview"
                                                                    isPreview={true}
                                                                    onClick={() => handleNestedCoursePreview(courseId)}
                                                                />
                                                            </motion.div>
                                                        </Grid>
                                                    );
                                                })}
                                            </Grid>
                                        </motion.div>
                                    </AnimatePresence>
                                </Grid>
                            </Grid>

                            {contentPack.courseIds?.length === 0 && (
                                <Box sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    p: 4
                                }}>
                                    <Typography color="textSecondary">
                                        No courses in this content pack
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
};
